export interface Preview {
	html?: string;
	error?: string;
}

interface Waiting {
	markdown: string;
	resolve: (preview: Preview | undefined) => void;
}

const CHUNK = 200;
const valid = (item: unknown): item is Preview => typeof item === "object" && item !== null && ("html" in item || "error" in item);

// What the reader sees for a piece of notation, drawn by the server. Requests made in the same tick
// go out as one POST; a fragment is asked for once. When the request fails nothing is cached and the
// caller keeps showing the raw notation.
export class Previews {
	private cache = new Map<string, Preview>();
	private waiting: Waiting[] = [];
	private scheduled = false;
	private abort = new AbortController();

	constructor(private url: string) {}

	cached(markdown: string): Preview | undefined {
		return this.cache.get(markdown);
	}

	// The editor is gone: what is in flight is cancelled and nothing more is asked for.
	stop() {
		this.abort.abort();
	}

	request(markdown: string): Promise<Preview | undefined> {
		const hit = this.cache.get(markdown);
		if (hit) return Promise.resolve(hit);
		if (this.abort.signal.aborted) return Promise.resolve(undefined);
		return new Promise((resolve) => {
			this.waiting.push({ markdown, resolve });
			if (this.scheduled) return;
			this.scheduled = true;
			queueMicrotask(() => void this.flush());
		});
	}

	private async fetch(fragments: string[]) {
		try {
			const response = await fetch(this.url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
				},
				body: JSON.stringify({ fragments }),
				signal: this.abort.signal,
			});
			const { results } = (await response.json()) as { results: unknown[] };
			fragments.forEach((fragment, i) => {
				if (valid(results[i])) this.cache.set(fragment, results[i]);
			});
		} catch {
			// The raw notation stays on screen.
		}
	}

	private async flush() {
		const batch = this.waiting.splice(0);
		this.scheduled = false;
		const fragments = [...new Set(batch.map((item) => item.markdown))];
		// The server takes at most this many fragments a request.
		for (let from = 0; from < fragments.length && !this.abort.signal.aborted; from += CHUNK) await this.fetch(fragments.slice(from, from + CHUNK));
		for (const item of batch) item.resolve(this.cache.get(item.markdown));
	}
}
